import PropTypes from 'prop-types'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ModalSidebarContext } from '../../../../../components/ModalSidebar'
import ViewCollectionModal from '../../../Companies/CollectionsPage/ViewCollectionModal'
import EditSystemUserPanel from '../EditSystemUserPanel/EditSystemUserPanel'
import SystemUserTab from '../SystemUserTab/SystemUserTab'
import useTechnicalPrimaryRole from '../useTechnicalPrimaryRole'

/**
 * System User tab of the user details panel, with the edit panel and collection view it opens on top.
 * The panel owns whether the edit panel is open, so it can open the tab straight into editing.
 */
function SystemUserSection({
  systemUserDetails,
  title,
  hasManagePermission = false,
  isEditing,
  onEditingChange,
  onSaved,
}) {
  const {
    systemUser,
    roleLabel,
    permissionLevels,
    permissionEntities,
    allCustomers,
    customerCollections,
    isFetchingCustomerCollections,
  } = systemUserDetails
  const { setToBackdrop } = useContext(ModalSidebarContext)
  const technicalPrimaryRole = useTechnicalPrimaryRole(systemUser.id)
  const [collectionToView, setCollectionToView] = useState(null)
  const canEdit = hasManagePermission && systemUser.chargebee_id == null
  const isEditOpen = isEditing && canEdit

  // The details panel steps behind whatever this tab opens on top of it.
  useEffect(() => {
    setToBackdrop(isEditOpen || !!collectionToView)
    return () => setToBackdrop(false)
  }, [isEditOpen, collectionToView, setToBackdrop])

  const collection = useMemo(
    () => customerCollections.find((item) => item.id === systemUser.collectionId) ?? null,
    [customerCollections, systemUser.collectionId],
  )

  const openEdit = useCallback(() => onEditingChange(true), [onEditingChange])
  const closeEdit = useCallback(() => onEditingChange(false), [onEditingChange])
  const closeCollection = useCallback(() => setCollectionToView(null), [])
  const handleSaved = useCallback(() => {
    onEditingChange(false)
    onSaved()
  }, [onEditingChange, onSaved])

  const canChangeTechnicalPrimary = technicalPrimaryRole.isAvailable && hasManagePermission

  return (
    <>
      <SystemUserTab
        user={systemUser}
        roleLabel={roleLabel}
        collection={collection}
        permissionLevels={permissionLevels}
        permissionEntities={permissionEntities}
        canEdit={canEdit}
        onEdit={openEdit}
        isTechnicalPrimary={technicalPrimaryRole.isTechnicalPrimary}
        isTechnicalPrimaryDisabled={technicalPrimaryRole.isSaving}
        onToggleTechnicalPrimary={canChangeTechnicalPrimary ? technicalPrimaryRole.toggleRole : null}
      />

      {isEditOpen && (
        <EditSystemUserPanel
          user={systemUser}
          title={title}
          hasManagePermission={hasManagePermission}
          permissionLevels={permissionLevels}
          permissionEntities={permissionEntities}
          allCustomers={allCustomers}
          customerCollections={customerCollections}
          isFetchingCustomerCollections={isFetchingCustomerCollections}
          setCollectionToView={setCollectionToView}
          toBackdrop={!!collectionToView}
          onHide={closeEdit}
          onSaved={handleSaved}
        />
      )}

      {collectionToView && <ViewCollectionModal show onHide={closeCollection} collection={collectionToView} />}
    </>
  )
}

SystemUserSection.propTypes = {
  /** Result of useSystemUserDetails, loaded with a system user */
  systemUserDetails: PropTypes.shape({
    systemUser: PropTypes.shape({
      id: PropTypes.number.isRequired,
      chargebee_id: PropTypes.string,
      collectionId: PropTypes.number,
    }).isRequired,
    roleLabel: PropTypes.string,
    permissionLevels: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
    permissionEntities: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
    allCustomers: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
    customerCollections: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
    isFetchingCustomerCollections: PropTypes.bool,
  }).isRequired,
  /** Panel title, reused by the edit panel */
  title: PropTypes.node.isRequired,
  hasManagePermission: PropTypes.bool,
  isEditing: PropTypes.bool.isRequired,
  onEditingChange: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
}

export default SystemUserSection
